import LegalPage from "@/pages/LegalPage";

export default function ReturnRefundPolicyPage() {
  return (
    <LegalPage
      title={<>Return &amp; Refund <em>Policy.</em></>}
      subtitle="Please read our Return & Refund Policy carefully before placing an order with Utsah."
      lastUpdated="29 July 2026"
      metaTitle="Return & Refund Policy | Utsah"
      metaDescription="Read Utsah's Return & Refund Policy covering order confirmation, payments, cancellations, returns, replacements and shipping."
    >
      <h2>1. Introduction</h2>
      <p>At Utsah, we strive to deliver premium gifting solutions with the highest standards of quality and service.</p>
      <p>As most of our products are customised or made-to-order, our return and refund policy differs from that of a standard retail business. Please read this policy carefully before placing an order.</p>

      <h2>2. Order Confirmation</h2>
      <p>An order is considered confirmed only after the required advance payment has been successfully received.</p>
      <p>A Purchase Order (PO), email confirmation or verbal confirmation alone does not constitute an accepted order.</p>

      <h2>3. Advance Payment</h2>
      <p>A 50% advance payment is mandatory to confirm every order.</p>
      <p>The advance payment requirement is fixed and cannot be reduced. Advance payments of less than 50%, including payments of 25%, 30% or any other amount, shall not be accepted unless expressly agreed by Utsah in writing.</p>
      <p>An order shall not be considered confirmed, and production, procurement or customisation shall not commence, until the full 50% advance payment has been received.</p>
      <p>The remaining 50% balance payment must be received in full before dispatch of the order.</p>

      <h2>4. Final Payment Before Dispatch</h2>
      <p>To help customers complete their internal approval and payment processes, Utsah may notify the customer one or more business days before the planned dispatch date.</p>
      <p>This advance notification is provided solely as a courtesy and does not alter the requirement that the outstanding balance must be received in full before dispatch.</p>
      <p>Utsah reserves the right to withhold dispatch until the full outstanding payment has been received.</p>

      <h2>5. Cancellation Before Production</h2>
      <p>Customers may request cancellation before production or procurement begins.</p>
      <p>Cancellation requests are subject to written confirmation by Utsah.</p>
      <p>If production has not yet commenced, refunds, if applicable, will be processed after deducting any costs already incurred.</p>

      <h2>6. Cancellation After Production Begins</h2>
      <p>Once production, procurement, branding, printing or customisation has commenced, orders cannot be cancelled without financial implications.</p>
      <p>If a cancellation is accepted after production has started, the advance payment shall remain non-refundable.</p>

      <h2>7. Customised Products</h2>
      <p>Due to the personalised nature of customised products, branded merchandise and printed packaging, customised items cannot be returned or exchanged unless they are defective or incorrectly supplied by Utsah.</p>
      <p>Customers are responsible for reviewing and approving all artwork, spellings, logos, colours, branding proofs and other customisation details before production begins.</p>
      <p>Once artwork has been approved, Utsah shall not be responsible for errors contained within the approved proof.</p>

      <h2>8. Returns</h2>
      <p>Returns will only be accepted if:</p>
      <ul>
        <li>Incorrect products were supplied.</li>
        <li>Products arrived with manufacturing defects.</li>
        <li>Products differ materially from the approved order specifications.</li>
      </ul>
      <p>Minor variations in colour, texture, grain, finish, dimensions or packaging resulting from manufacturing processes, natural materials or printing processes shall not be considered defects.</p>

      <h2>9. Reporting Damaged or Incorrect Products</h2>
      <p>Customers must notify Utsah within 24 hours of receiving their order if they believe products have been supplied incorrectly or contain manufacturing defects.</p>
      <p>The notification should include:</p>
      <ul>
        <li>Order details</li>
        <li>Clear photographs of the products</li>
        <li>Description of the issue</li>
      </ul>
      <p>Failure to report the issue within this period may result in the request being declined.</p>
      <p>Claims relating to damage caused after delivery due to storage, misuse, improper handling or customer negligence shall not be eligible for return, replacement or refund.</p>

      <h2>10. Inspection &amp; Resolution</h2>
      <p>Upon receiving a claim, Utsah will review the information provided and may request additional photographs or supporting information.</p>
      <p>If the claim is approved, Utsah may, at its sole discretion:</p>
      <ul>
        <li>Replace the affected products;</li>
        <li>Repair the products where feasible; or</li>
        <li>Issue an appropriate refund or credit.</li>
      </ul>
      <p>The chosen resolution shall be determined by Utsah based on the nature of the issue.</p>

      <h2>11. Non-Returnable Items</h2>
      <p>The following items are not eligible for return or refund unless defective:</p>
      <ul>
        <li>Custom branded products</li>
        <li>Personalised products</li>
        <li>Printed packaging</li>
        <li>Approved artwork orders</li>
        <li>Products damaged after delivery</li>
        <li>Products showing misuse or improper handling</li>
      </ul>

      <h2>12. Shipping Charges</h2>
      <p>Unless expressly stated otherwise in the quotation or invoice, all prices quoted by Utsah are for the products only and do not include shipping, freight, courier, transportation, handling or insurance charges.</p>
      <p>Applicable shipping charges shall be communicated separately and shall be borne by the customer unless otherwise agreed in writing.</p>

      <h2>13. Shipping Responsibility</h2>
      <p>Risk of loss or damage transfers to the customer once the shipment has been handed over to the shipping partner or transporter.</p>
      <p>While Utsah will reasonably assist customers in resolving shipment-related concerns, Utsah shall not be responsible for delays, loss or damage occurring during transit.</p>

      <h2>14. Refund Processing</h2>
      <p>Where a refund has been approved, it will be processed using the original payment method or another mutually agreed payment method.</p>
      <p>Refund processing timelines may vary depending on the customer's financial institution or payment provider.</p>

      <h2>15. Contact Us</h2>
      <p>For any questions regarding this Return &amp; Refund Policy, please contact:</p>
      <p>
        Kiran Ventures (Utsah)<br />
        54 Vinay Nagar<br />
        Kesar Bagh Road<br />
        Indore, Madhya Pradesh 452009<br />
        India
      </p>
      <p>Legal Email: <a href="mailto:atishaya@utsahgifting.com">atishaya@utsahgifting.com</a></p>
      <p>Customer Support: <a href="mailto:pragati@utsahgifting.com">pragati@utsahgifting.com</a></p>
      <p>Phone: +91 90988 80482</p>
    </LegalPage>
  );
}
